interface SkeletonCardProps {
  /** Number of cards to render. */
  count?: number;
  /** Minimum card height in px — keep close to the real card to avoid layout shift (CLS). */
  minHeight?: number;
  /** Render a stats strip inside the card, as the active provider card has. */
  withStats?: boolean;
}

/**
 * Card-shaped loading placeholder.
 *
 * Required by UI_UX_STANDARDS.md, which forbids spinners for initial page
 * loads and requires a skeleton component to exist before the corresponding
 * card-based screen is implemented. Uses the shared `skeleton-shimmer` class,
 * so light/dark tokens and the 1.2–1.5s shimmer cycle stay consistent with
 * SkeletonTable and the other skeletons.
 */
export function SkeletonCard({ count = 3, minHeight = 120, withStats = false }: SkeletonCardProps) {
  return (
    <div className="space-y-4" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-2xl border border-border bg-card"
          style={{ minHeight }}
        >
          <div className="flex items-start gap-4 p-5">
            <div className="skeleton-shimmer h-12 w-12 shrink-0 rounded-xl" />
            <div className="flex-1 space-y-2">
              <div className="skeleton-shimmer h-4 w-40 rounded" />
              <div className="skeleton-shimmer h-3 w-28 rounded" />
            </div>
            <div className="skeleton-shimmer h-8 w-24 shrink-0 rounded-lg" />
          </div>

          {withStats && i === 0 && (
            <div className="grid grid-cols-2 gap-px border-t border-border bg-border sm:grid-cols-4">
              {Array.from({ length: 4 }).map((_, s) => (
                <div key={s} className="space-y-2 bg-card px-4 py-3">
                  <div className="skeleton-shimmer h-3 w-16 rounded" />
                  <div className="skeleton-shimmer h-4 w-20 rounded" />
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 border-t border-border px-5 py-4">
            <div className="skeleton-shimmer h-9 w-32 rounded-lg" />
            <div className="skeleton-shimmer h-9 w-24 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}
